/**
 * W1-11b (app-shell-ui): renders the panels modules register for a slot (ARCHITECTURE.md §6.2; DESIGN.md §3.2).
 *
 *  - Panels render in `order`; Full-level panels (Raw...) are offered only at the Full level (Guided hides tools,
 *    never data: their commands stay in the palette).
 *  - Tabbed slots (the Chart dock: Lines, Problems, History, Assets, Raw) show one panel at a time; the chosen tab
 *    is remembered per user and `ui.showPanel(id)` selects it.
 *  - Other slots stack their panels; with more than one, each has a collapsible header, starting collapsed in the
 *    layout presets listed in its `collapsedIn`.
 *  - Each panel has its own error boundary: a crash shows "This panel stopped working" with a reload button and the
 *    rest of the editor keeps working. An empty slot explains what will appear there.
 * The wrapper carries `data-slot` (used by `ui.focusRegion`); panels put `data-region` on their own root.
 *
 * `InlineSlot` hosts the global slots inside the bars (`topbar.left`, `topbar.center`, `topbar.right`, `statusbar`):
 * the panels sit inline in the row, in order, each in its own silent error boundary; nothing is drawn while the slot
 * is empty (the bars never show placeholders).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { LayoutPreset } from '../../state/types';
import { EmptyState } from '../../ui/EmptyState';
import { Icon } from '../../ui/Icon';
import { cx } from '../../ui/internal/cx';
import { Tabs } from '../../ui/Tabs';
import type { AppServices, PanelDef, PanelSlot } from '../registry/types';
import type { ShellUiService, UiSnapshot } from '../uiService';
import { ErrorBoundary } from './ErrorBoundary';
import { selectKey } from './selectKey';
import { useSettingsValue, useText, useUi } from './ShellContext';
import { m } from './shell.messages';
import l from './Layout.module.css';

export const TABBED_SLOTS: ReadonlySet<PanelSlot> = new Set<PanelSlot>(['chart.dock']);

/** Slots drawn inside the top bar and the hint bar rather than in the workspace. */
export type InlinePanelSlot = Extract<PanelSlot, 'topbar.left' | 'topbar.center' | 'topbar.right' | 'statusbar'>;

/** Panels of a slot visible at a level. */
export function visiblePanels(services: AppServices, slot: PanelSlot, level: 'guided' | 'full'): readonly PanelDef[] {
  const all = services.registry.panels(slot);
  return level === 'full' ? all : all.filter((p) => p.level === 'guided');
}

export interface SlotHostProps {
  readonly services: AppServices;
  readonly ui: ShellUiService;
  readonly slot: PanelSlot;
  readonly preset: LayoutPreset;
  readonly className?: string | undefined;
}

function PanelView(props: { readonly panel: PanelDef; readonly services: AppServices }) {
  const { panel, services } = props;
  const Component = panel.component;
  return (
    <ErrorBoundary variant="panel" services={services} label={panel.id}>
      <Component services={services} />
    </ErrorBoundary>
  );
}

export function SlotHost(props: SlotHostProps) {
  const { services, ui, slot, preset } = props;
  const text = useText(services);
  const level = useSettingsValue(services).general.level;
  const panels = visiblePanels(services, slot, level);
  const tabbed = TABBED_SLOTS.has(slot);

  let body: ReactNode;
  if (panels.length === 0) {
    body = (
      <EmptyState
        icon="LayoutPanelTop"
        title={{ key: m.slotEmptyTitle }}
        body={{ key: m.slotEmpty, params: { slot: selectKey(slot) } }}
      />
    );
  } else if (tabbed) {
    body = <TabbedPanels services={services} ui={ui} slot={slot} panels={panels} />;
  } else if (panels.length === 1 && panels[0] !== undefined) {
    body = <PanelView panel={panels[0]} services={services} />;
  } else {
    body = <StackedPanels services={services} ui={ui} slot={slot} panels={panels} preset={preset} />;
  }
  return (
    <section
      className={cx(l.slot, props.className)}
      data-slot={slot}
      aria-label={text.t(m.slotLabel, { slot: selectKey(slot) })}
      {...(tabbed ? { 'data-region': 'dock' } : {})}
    >
      {body}
    </section>
  );
}

/** The panels of a global slot, inline in their bar (nothing while the slot is empty). */
export function InlineSlot(props: {
  readonly services: AppServices;
  readonly slot: InlinePanelSlot;
  readonly className?: string | undefined;
}) {
  const { services, slot } = props;
  const text = useText(services);
  const level = useSettingsValue(services).general.level;
  const panels = visiblePanels(services, slot, level);
  if (panels.length === 0) return null;
  return (
    <div
      className={cx(l.inlineSlot, props.className)}
      data-slot={slot}
      role="group"
      aria-label={text.t(m.slotLabel, { slot: selectKey(slot) })}
    >
      {panels.map((p) => {
        const Component = p.component;
        return (
          <ErrorBoundary key={p.id} variant="silent" services={services} label={p.id}>
            <Component services={services} />
          </ErrorBoundary>
        );
      })}
    </div>
  );
}

const selectTabs = (snap: UiSnapshot): UiSnapshot['activeTabs'] => snap.activeTabs;

function TabbedPanels(props: {
  readonly services: AppServices;
  readonly ui: ShellUiService;
  readonly slot: PanelSlot;
  readonly panels: readonly PanelDef[];
}) {
  const { services, ui, slot, panels } = props;
  const text = useText(services);
  const tabs = useUi(ui, selectTabs);
  const chosen = tabs[slot];
  const active = panels.find((p) => p.id === chosen) ?? panels[0];
  if (active === undefined) return null;
  return (
    <Tabs<string>
      className={cx(l.tabs)}
      variant="line"
      label={text.t(m.slotLabel, { slot: selectKey(slot) })}
      value={active.id}
      onChange={(id) => {
        ui.setActiveTab(slot, id);
      }}
      tabs={panels.map((p) => ({
        value: p.id,
        label: text.t(p.title),
        ...(p.icon === undefined ? {} : { icon: p.icon }),
      }))}
    >
      <div className={l.tabPanel}>
        <PanelView key={active.id} panel={active} services={services} />
      </div>
    </Tabs>
  );
}

const selectRequest = (snap: UiSnapshot): UiSnapshot['panelRequest'] => snap.panelRequest;

function StackedPanels(props: {
  readonly services: AppServices;
  readonly ui: ShellUiService;
  readonly slot: PanelSlot;
  readonly panels: readonly PanelDef[];
  readonly preset: LayoutPreset;
}) {
  const { services, ui, slot, panels, preset } = props;
  const text = useText(services);
  const request = useUi(ui, selectRequest);
  const [open, setOpen] = useState<Readonly<Record<string, boolean>>>({});
  const refs = useRef(new Map<string, HTMLElement>());
  const isOpen = (p: PanelDef): boolean => open[p.id] ?? !(p.collapsedIn ?? []).includes(preset);

  // showPanel(id) for a stacked panel: expand it and bring it into view.
  const [handled, setHandled] = useState(0);
  if (request !== null && request.slot === slot && request.nonce !== handled) {
    setHandled(request.nonce);
    if (open[request.id] !== true) setOpen((o) => ({ ...o, [request.id]: true }));
  }
  useEffect(() => {
    if (request?.slot !== slot) return;
    const el = refs.current.get(request.id) as Partial<Pick<HTMLElement, 'scrollIntoView'>> | undefined;
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [request, slot]);

  return (
    <div className={l.stack}>
      {panels.map((p) => {
        const expanded = isOpen(p);
        const title = text.t(p.title);
        return (
          <div
            key={p.id}
            className={l.stackItem}
            data-open={expanded ? 'true' : undefined}
            ref={(el) => {
              if (el === null) refs.current.delete(p.id);
              else refs.current.set(p.id, el);
            }}
          >
            <button
              type="button"
              className={l.stackHeader}
              aria-expanded={expanded}
              aria-label={text.t(m.sectionToggle, { open: expanded ? 'yes' : 'no', title })}
              onClick={() => {
                setOpen((o) => ({ ...o, [p.id]: !expanded }));
              }}
            >
              <Icon name={expanded ? 'ChevronDown' : 'ChevronRight'} size={14} />
              {p.icon !== undefined && <Icon name={p.icon} size={14} />}
              <span>{title}</span>
            </button>
            {expanded && (
              <div className={l.stackBody}>
                <PanelView panel={p} services={services} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
